import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { $getNearestBlockElementAncestorOrThrow } from "@lexical/utils";
import {
  $createRangeSelection,
  $getSelection,
  $isBlockElementNode,
  $isRangeSelection,
  $normalizeSelection__EXPERIMENTAL,
  COMMAND_PRIORITY_EDITOR,
  INDENT_CONTENT_COMMAND,
  INSERT_TAB_COMMAND,
  KEY_TAB_COMMAND,
  OUTDENT_CONTENT_COMMAND,
} from "lexical";
import { useEffect } from "react";

import type { RangeSelection } from "lexical";

function shouldIndent(selection: RangeSelection): boolean {
  const nodes = selection
    .getNodes()
    .filter((node) => $isBlockElementNode(node) && node.canIndent());

  // If selection spans across canIndent block nodes: indent
  if (nodes.length > 0) {
    return true;
  }

  // If first (anchor/focus) is at block start: indent
  const first = selection.focus.isBefore(selection.anchor) ? selection.focus : selection.anchor;
  const firstBlock = $getNearestBlockElementAncestorOrThrow(first.getNode());
  if (firstBlock.canIndent()) {
    const firstBlockKey = firstBlock.getKey();
    const selectionAtStart = $createRangeSelection();
    selectionAtStart.anchor.set(firstBlockKey, 0, "element");
    selectionAtStart.focus.set(firstBlockKey, 0, "element");
    return $normalizeSelection__EXPERIMENTAL(selectionAtStart).anchor.is(first);
  }

  return false;
}

export function TabIndentationPlugin(): null {
  const [editor] = useLexicalComposerContext();
  useEffect(() =>
    editor.registerCommand<KeyboardEvent>(
      KEY_TAB_COMMAND,
      (event) => {
        const selection = $getSelection();
        if (!$isRangeSelection(selection)) {
          return false;
        }

        event.preventDefault();
        return editor.dispatchCommand(
          shouldIndent(selection)
            ? event.shiftKey
              ? OUTDENT_CONTENT_COMMAND
              : INDENT_CONTENT_COMMAND
            : INSERT_TAB_COMMAND,
          undefined,
        );
      },
      COMMAND_PRIORITY_EDITOR,
    ),
  );

  return null;
}
